// Dev-only preset editor. Authors / edits text presets by dragging components
// onto a canvas. Saves to src/pages/app/lib/templates.json via the Vite dev
// middleware in vite.config.ts. Not part of the user-facing wizard — open at
// /preset-editor in dev.
//
// State model:
//   - `templates`: the working set of all presets (built-in + user). Edits
//     happen in this array.
//   - `selectedId`: which template is loaded into the canvas.
//   - `selectedLayerIdx`: which layer in the loaded template is highlighted
//     for delete / drag visualization.
//   - `aspectRatio`: '1:1' | '4:5'. Drives canvas shape only; the layers
//     stored on disk are aspect-agnostic and rendered against whichever
//     aspect is active.
//
// Persistence: on Save, we diff the working set against BUILT_IN_PRESETS by
// JSON content. Anything that differs (or is new) ends up in templates.json;
// anything matching built-in is omitted so the file stays minimal and
// "reset to factory" is just "delete the user entry."

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type AspectRatio,
  type Layer,
  type PresetLayer,
  type PresetLayout,
  type ShapeLayer,
  type TextPreset,
  REFERENCE_CANVAS_WIDTH,
  aspectClass,
  aspectToRefHeight,
  clampAnchor,
  getTextLayerStyle,
  resolveStackedLayers,
} from '../app/lib/composeRender';
import { COMPONENT_CATALOG } from '../app/lib/components';
import { BUILT_IN_PRESETS, TEXT_PRESETS } from '../app/lib/textPresets';

const SAMPLE_BG = '/templates/template3/frame1_thumb.png';

// Sample copy that drives the editor canvas. Lets us stress-test layouts
// against the same length spectrum the AI produces in production. Not part
// of templates.json — purely editor-local state, persisted to localStorage.
interface SampleCopy {
  headline: string;
  tagline: string;
  subtext: string;
  cta: string;
}

const DEFAULT_SAMPLE_COPY: SampleCopy = {
  headline: 'Your Bold Headline',
  tagline: 'A supporting line of copy',
  subtext: 'A bit more detail here',
  cta: 'SHOP NOW',
};

// Quick-select length presets — one click to swap the whole copy set so we
// can verify the layout doesn't break across the realistic AI-copy length
// range. We cap at "Long" since the production AI copy will be length-limited.
const SAMPLE_COPY_PRESETS: Record<string, SampleCopy> = {
  Short: {
    headline: 'Be Bold.',
    tagline: 'Now.',
    subtext: 'Today only.',
    cta: 'GO',
  },
  Medium: DEFAULT_SAMPLE_COPY,
  Long: {
    headline: 'A Slightly Longer Headline That Tests Wrap',
    tagline: 'A longer tagline that takes up more horizontal space',
    subtext: 'Extra detail meant to push the layout into a second line',
    cta: 'SHOP NOW',
  },
};

const SAMPLE_COPY_STORAGE_KEY = 'preset-editor:sample-copy';

function loadSampleCopy(): SampleCopy {
  if (typeof window === 'undefined') return DEFAULT_SAMPLE_COPY;
  try {
    const raw = window.localStorage.getItem(SAMPLE_COPY_STORAGE_KEY);
    if (!raw) return DEFAULT_SAMPLE_COPY;
    const parsed = JSON.parse(raw);
    return {
      headline: parsed.headline ?? DEFAULT_SAMPLE_COPY.headline,
      tagline: parsed.tagline ?? DEFAULT_SAMPLE_COPY.tagline,
      subtext: parsed.subtext ?? DEFAULT_SAMPLE_COPY.subtext,
      cta: parsed.cta ?? DEFAULT_SAMPLE_COPY.cta,
    };
  } catch {
    return DEFAULT_SAMPLE_COPY;
  }
}

// aspectClass moved to composeRender.ts (must list every ratio as a static
// literal so Tailwind JIT picks them up — dynamic class strings are invisible).

// Convert a PresetLayer → a renderable Layer with resolved `text` from the
// editor's sample copy. CTA layers prefer the sample CTA over `staticText` so
// users can stress-test long button labels (e.g. "BUY NOW WITH DISCOUNT").
function resolvePresetLayer(layer: PresetLayer, copy: SampleCopy): Layer {
  if (layer.kind === 'shape') return layer;
  let text = '';
  if (layer.role === 'static') text = layer.staticText ?? '';
  else if (layer.role === 'cta') text = copy.cta;
  else if (layer.role === 'headline') text = copy.headline;
  else if (layer.role === 'tagline') text = copy.tagline;
  else if (layer.role === 'subtext') text = copy.subtext;
  return { ...layer, kind: 'text', text };
}

// Slug a name into an id ("Cream Bars Variant" → "cream-bars-variant").
function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `template-${Date.now()}`;
}

// Persist the FULL ordered list. We no longer skip "matches-factory" entries
// because the JSON's order is itself meaningful (it drives gallery order),
// so omitting matched entries would lose the user's reorder intent. Reset-
// to-factory still works — it replaces the entry's content with the factory
// version, but the entry stays in JSON to hold its position.
function computeUserTemplates(all: TextPreset[]): TextPreset[] {
  return all;
}

export default function PresetEditorPage() {
  // Working copy of all templates. Initialized from the merged view so user
  // overrides from templates.json show up immediately.
  const [templates, setTemplates] = useState<TextPreset[]>(() => structuredClone(TEXT_PRESETS));
  const [selectedId, setSelectedId] = useState<string | null>(templates[0]?.id ?? null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [selectedLayerIdx, setSelectedLayerIdx] = useState<number | null>(null);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  // Sample copy drives layer text in the canvas — purely editor-local state.
  const [sampleCopy, setSampleCopy] = useState<SampleCopy>(loadSampleCopy);
  const [sampleCopyOpen, setSampleCopyOpen] = useState(false);
  useEffect(() => {
    try { window.localStorage.setItem(SAMPLE_COPY_STORAGE_KEY, JSON.stringify(sampleCopy)); } catch { /* ignore */ }
  }, [sampleCopy]);
  // Inline rename + drag-reorder state for the templates list.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [dragSrcIdx, setDragSrcIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const dragBoxRef = useRef<{ w: number; h: number } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const templatesRef = useRef(templates); templatesRef.current = templates;
  const selectedIdRef = useRef(selectedId); selectedIdRef.current = selectedId;
  const draggingIdxRef = useRef(draggingIdx); draggingIdxRef.current = draggingIdx;

  const selected = templates.find((t) => t.id === selectedId) ?? null;
  const layout: PresetLayout | undefined = selected?.layouts[aspectRatio];
  const layers = layout?.layers ?? [];

  // Resolve layer text + live stacking so the canvas reflects what the
  // production renderer will draw. resolveStackedLayers returns a new array
  // with stack-on-previous layers reflowed; we render this view, while
  // dragging continues to mutate the underlying (unresolved) `layers`.
  const refH = aspectToRefHeight(aspectRatio);
  const resolvedLayers = useMemo(() => {
    const withText = layers.map((l) => resolvePresetLayer(l, sampleCopy));
    return resolveStackedLayers(withText, REFERENCE_CANVAS_WIDTH, refH);
  }, [layers, sampleCopy, refH]);

  // ---- Mutate the active template's layers ----
  const updateLayers = (mutator: (prev: PresetLayer[]) => PresetLayer[]) => {
    if (!selected) return;
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.id !== selected.id) return t;
        const existing = t.layouts[aspectRatio]?.layers ?? [];
        const next = mutator(existing);
        return {
          ...t,
          layouts: { ...t.layouts, [aspectRatio]: { layers: next } },
        };
      }),
    );
  };

  // ---- Component palette: click to add ----
  const handleAddComponent = (id: string) => {
    const entry = COMPONENT_CATALOG.find((c) => c.id === id);
    if (!entry || !selected) return;
    const added: PresetLayer[] = entry.kind === 'multi' ? entry.factory() : [entry.factory()];
    updateLayers((prev) => [...prev, ...added]);
    setSelectedLayerIdx(layers.length + added.length - 1);
  };

  // ---- Layer operations ----
  const handleDeleteLayer = (idx: number) => {
    updateLayers((prev) => prev.filter((_, i) => i !== idx));
    setSelectedLayerIdx(null);
  };

  const handleMoveLayer = (idx: number, x: number, y: number) => {
    updateLayers((prev) => prev.map((l, i) => (i === idx ? { ...l, x, y } : l)));
  };

  // When the user starts dragging a stacked text layer, bake the resolved y
  // into the stored layer and clear stackOnPrevious so subsequent renders
  // honor the drag instead of overriding it with the stack position.
  const handleBreakStack = (idx: number, resolvedY: number) => {
    updateLayers((prev) =>
      prev.map((l, i) =>
        i === idx && l.kind === 'text' && l.stackOnPrevious
          ? { ...l, y: resolvedY, stackOnPrevious: false }
          : l,
      ),
    );
  };

  // Toggle coupling. When turning ON: set the flag + default gap if unset.
  // When turning OFF: bake the resolved (visible) y so the layer doesn't jump.
  const handleToggleCoupled = (idx: number) => {
    const stored = templatesRef.current.find((t) => t.id === selectedIdRef.current)
      ?.layouts[aspectRatio]?.layers[idx];
    if (!stored || stored.kind !== 'text') return;
    if (stored.stackOnPrevious) {
      const resolved = resolvedLayers[idx];
      const bakedY = resolved && resolved.kind === 'text' ? resolved.y : stored.y;
      updateLayers((prev) =>
        prev.map((l, i) =>
          i === idx && l.kind === 'text'
            ? { ...l, y: bakedY, stackOnPrevious: false }
            : l,
        ),
      );
    } else {
      updateLayers((prev) =>
        prev.map((l, i) =>
          i === idx && l.kind === 'text'
            ? { ...l, stackOnPrevious: true, stackGap: l.stackGap ?? 24 }
            : l,
        ),
      );
    }
  };

  const handleSetGap = (idx: number, gap: number) => {
    updateLayers((prev) =>
      prev.map((l, i) =>
        i === idx && l.kind === 'text' ? { ...l, stackGap: gap } : l,
      ),
    );
  };

  // Snap the layer's horizontal alignment. side='l' → left-anchored at the
  // safe margin, 'c' → centered on x=0.5, 'r' → right-anchored at the right
  // safe margin. Also adjusts the anchor's horizontal half + textAlign so the
  // bar grows in the right direction and the text inside reads naturally.
  const handleHorizontalAlign = (idx: number, side: 'l' | 'c' | 'r') => {
    const MARGIN = 0.04; // matches OVERLAY_PAD_FRAC
    const targetX = side === 'l' ? MARGIN : side === 'r' ? 1 - MARGIN : 0.5;
    const textAlign: 'left' | 'center' | 'right' =
      side === 'l' ? 'left' : side === 'r' ? 'right' : 'center';
    updateLayers((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        if (l.kind === 'shape') {
          // Position the shape's bounding box relative to the chosen side.
          const x = side === 'l' ? MARGIN
            : side === 'r' ? 1 - MARGIN - l.w
            : 0.5 - l.w / 2;
          return { ...l, x };
        }
        // Rewrite the anchor's horizontal half ('l' | 'c' | 'r').
        const vert = l.anchor.charAt(0) as 't' | 'm' | 'b';
        const newAnchor = `${vert}${side}` as typeof l.anchor;
        return { ...l, x: targetX, anchor: newAnchor, textAlign };
      }),
    );
  };

  // Coupling requires a previous text layer to bond to. Shapes don't count
  // as stack bases — only text layers form the chain.
  const hasPreviousTextLayer = (idx: number): boolean => {
    for (let i = 0; i < idx; i++) {
      if (layers[i]?.kind === 'text') return true;
    }
    return false;
  };

  // ---- Drag (canonical pattern from StepText) ----
  useEffect(() => {
    if (draggingIdx === null) return;
    const updateFromPoint = (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;
      const cursorX = (clientX - rect.left) / rect.width;
      const cursorY = (clientY - rect.top) / rect.height;
      const off = dragOffsetRef.current;
      const targetX = off ? cursorX - off.dx : cursorX;
      const targetY = off ? cursorY - off.dy : cursorY;
      const box = dragBoxRef.current;
      const wFrac = box ? box.w / rect.width : 0;
      const hFrac = box ? box.h / rect.height : 0;
      const sel = templatesRef.current.find((t) => t.id === selectedIdRef.current);
      const layer = sel?.layouts[aspectRatio]?.layers[draggingIdxRef.current ?? -1];
      const anchor = layer && layer.kind === 'text' ? layer.anchor : 'tl';
      const { x, y } = clampAnchor(targetX, targetY, wFrac, hFrac, anchor);
      handleMoveLayer(draggingIdxRef.current!, x, y);
    };
    const onMouseMove = (e: MouseEvent) => updateFromPoint(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) updateFromPoint(t.clientX, t.clientY);
    };
    const onUp = () => {
      setDraggingIdx(null);
      dragBoxRef.current = null;
      dragOffsetRef.current = null;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingIdx, aspectRatio]);

  // ---- Track canvas width for accurate preview font scaling ----
  const [canvasW, setCanvasW] = useState(0);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => setCanvasW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const previewFontScale = canvasW > 0 ? canvasW / REFERENCE_CANVAS_WIDTH : 1;

  // ---- New template ----
  const handleNewTemplate = () => {
    const name = window.prompt('Name for the new template?', '')?.trim();
    if (!name) return;
    const id = slugify(name);
    if (templates.find((t) => t.id === id)) {
      alert(`A template with id "${id}" already exists. Pick a different name.`);
      return;
    }
    const fresh: TextPreset = {
      id,
      name,
      layouts: { [aspectRatio]: { layers: [] } } as Record<AspectRatio, PresetLayout>,
    };
    setTemplates((prev) => [...prev, fresh]);
    setSelectedId(id);
    setSelectedLayerIdx(null);
  };

  // ---- Delete template ----
  const handleDeleteTemplate = (id: string) => {
    if (!window.confirm(`Delete "${templates.find((t) => t.id === id)?.name}"?`)) return;
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ---- Duplicate template ----
  // Clones the source template (deep), gives it a unique id by suffixing
  // `-copy` (and `-copy-2`, etc. if collisions), inserts it just below the
  // source, and selects it so the user can immediately edit.
  const handleDuplicateTemplate = (id: string) => {
    const srcIdx = templates.findIndex((t) => t.id === id);
    if (srcIdx < 0) return;
    const src = templates[srcIdx];
    let newId = `${id}-copy`;
    let n = 2;
    while (templates.some((t) => t.id === newId)) {
      newId = `${id}-copy-${n}`;
      n++;
    }
    const copy: TextPreset = structuredClone(src);
    copy.id = newId;
    copy.name = `${src.name} Copy`;
    setTemplates((prev) => [
      ...prev.slice(0, srcIdx + 1),
      copy,
      ...prev.slice(srcIdx + 1),
    ]);
    setSelectedId(newId);
    setSelectedLayerIdx(null);
  };

  // ---- Renumber all ----
  // Sequentially renames every template to `Text Template <index>` based on
  // current order. After this, "Template 5" unambiguously means the 5th row.
  // Run this when names have drifted (e.g. after reorders, renames, or
  // duplicate-and-rename cycles produced gaps like "Text Template 55").
  const handleRenumber = () => {
    if (!window.confirm(
      `Renumber all ${templates.length} templates as "Text Template 1, 2, 3, …" based on their current order? Any custom names will be overwritten. (Click Save All afterward to persist.)`,
    )) return;
    setTemplates((prev) =>
      prev.map((t, i) => ({ ...t, name: `Text Template ${i + 1}` })),
    );
  };

  // ---- Inline rename ----
  // The row's name swaps to an <input> when renamingId matches that template.
  // Triggered from the pencil icon in the row's hover actions.
  const handleStartRename = (id: string) => setRenamingId(id);
  const handleSaveRename = (id: string, raw: string) => {
    const next = raw.trim();
    if (next) {
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, name: next } : t)));
    }
    setRenamingId(null);
  };
  const handleCancelRename = () => setRenamingId(null);

  // ---- Drag-to-reorder ----
  // Native HTML5 DND. The whole row is draggable; dropping onto another row
  // splices the dragged template into that row's position. Visual feedback:
  // source row fades, drop target row tints.
  const handleRowDragStart = (idx: number) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers refuse to start a drag without setData; the payload is
    // unused (we read state) but it must be non-empty.
    e.dataTransfer.setData('text/plain', String(idx));
    setDragSrcIdx(idx);
  };
  const handleRowDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };
  const handleRowDrop = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const src = dragSrcIdx;
    setDragSrcIdx(null);
    setDragOverIdx(null);
    if (src === null || src === idx) return;
    setTemplates((prev) => {
      const next = [...prev];
      const [moved] = next.splice(src, 1);
      next.splice(idx, 0, moved);
      return next;
    });
  };
  const handleRowDragEnd = () => {
    setDragSrcIdx(null);
    setDragOverIdx(null);
  };

  // ---- Persist ----
  // Save All re-slugs each template's id from its current name so id ≡ slug(name).
  // After save, "Text Template 5" always has id "text-template-5". If two
  // templates would slug to the same id (duplicate names), a `-2`, `-3` …
  // suffix is appended to break the collision and the user is informed.
  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const usedIds = new Set<string>();
      const collisions: string[] = [];
      const userTemplates = templates.map((t, i) => {
        const base = slugify(t.name) || `template-${i + 1}`;
        let id = base;
        let n = 2;
        while (usedIds.has(id)) {
          id = `${base}-${n}`;
          n++;
        }
        usedIds.add(id);
        if (id !== base) collisions.push(`${t.name} → ${id}`);
        return { ...t, id };
      });
      const payload = computeUserTemplates(userTemplates);
      const res = await fetch('/api/templates-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      // Sync local state with the re-slugged ids so subsequent edits work
      // off the same identity that's now persisted on disk.
      setTemplates(userTemplates);
      // Keep selection valid if the selected template got a new id.
      if (selectedId) {
        const before = templates.findIndex((t) => t.id === selectedId);
        if (before >= 0) setSelectedId(userTemplates[before].id);
      }
      setSaveStatus('success');
      if (collisions.length > 0) {
        setSaveError(`Duplicate names found, auto-suffixed: ${collisions.join(', ')}`);
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : String(err));
    }
  };

  // ---- Reset selected to built-in (if applicable) ----
  const builtIn = selected ? BUILT_IN_PRESETS.find((p) => p.id === selected.id) : undefined;
  const isModified = !!(builtIn && JSON.stringify(builtIn) !== JSON.stringify(selected));
  const handleResetToBuiltIn = () => {
    if (!builtIn) return;
    if (!window.confirm(`Reset "${builtIn.name}" to factory default?`)) return;
    setTemplates((prev) => prev.map((t) => (t.id === builtIn.id ? structuredClone(builtIn) : t)));
    setSelectedLayerIdx(null);
  };

  return (
    <div className="h-screen w-full flex flex-col bg-surface">
      {/* Top bar */}
      <header className="h-14 flex-shrink-0 bg-white border-b border-surface-dark/40 flex items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
            <i className="ri-edit-box-line text-white text-sm" />
          </div>
          <h1 className="font-display font-bold text-dark text-base">Preset Editor</h1>
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">DEV</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value as AspectRatio)}
            className="px-3 py-1.5 rounded-lg border border-surface-dark/40 bg-white text-xs font-semibold text-dark cursor-pointer"
          >
            <option value="1:1">1:1 (Square)</option>
            <option value="4:5">4:5 (Portrait)</option>
            <option value="2:3">2:3 (Portrait tall)</option>
            <option value="9:16">9:16 (Story)</option>
            <option value="1.91:1">1.91:1 (Link card)</option>
            <option value="16:9">16:9 (Landscape)</option>
          </select>
          <button
            onClick={handleRenumber}
            title="Rename all templates as 'Text Template 1, 2, 3, …' based on current order"
            className="px-3 py-1.5 rounded-lg border border-surface-dark/40 bg-white text-xs font-semibold text-dark hover:border-primary hover:text-primary transition-colors cursor-pointer"
          >
            <i className="ri-list-ordered mr-1" />Renumber
          </button>
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-primary to-fuchsia-500 text-white text-xs font-bold shadow-md shadow-primary/25 hover:shadow-lg cursor-pointer disabled:opacity-60"
          >
            {saveStatus === 'saving' && <><i className="ri-loader-4-line animate-spin mr-1" />Saving…</>}
            {saveStatus === 'success' && <><i className="ri-check-line mr-1" />Saved</>}
            {saveStatus === 'error' && <><i className="ri-error-warning-line mr-1" />Retry</>}
            {saveStatus === 'idle' && <><i className="ri-save-line mr-1" />Save All</>}
          </button>
          <a
            href="/create"
            className="px-3 py-1.5 rounded-lg text-xs text-dark-muted hover:bg-surface hover:text-dark transition-colors"
          >
            Exit
          </a>
        </div>
      </header>

      {saveError && (
        <div className="flex-shrink-0 px-5 py-2 bg-red-50 border-b border-red-200 text-red-700 text-xs">
          Save failed: {saveError}
        </div>
      )}

      {/* Main grid: palette | canvas | template list */}
      <div className="flex-1 min-h-0 grid grid-cols-12 gap-3 p-3">
        {/* LEFT — component palette */}
        <aside className="col-span-2 flex flex-col min-h-0 bg-white rounded-2xl border border-surface-dark/40 overflow-hidden">
          <div className="flex-shrink-0 px-4 py-3 border-b border-surface-dark/30">
            <h3 className="font-display font-bold text-dark text-xs">Components</h3>
            <p className="text-xs text-dark-muted leading-tight mt-0.5">Click to add to canvas</p>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-3">
            {(['text', 'cta', 'shape'] as const).map((group) => (
              <div key={group}>
                <p className="text-xs font-bold text-dark-muted uppercase tracking-wide px-2 mb-1">
                  {group === 'cta' ? 'CTAs' : group === 'text' ? 'Text' : 'Shapes'}
                </p>
                <div className="space-y-1">
                  {COMPONENT_CATALOG.filter((c) => c.group === group).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleAddComponent(c.id)}
                      disabled={!selected}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-dark hover:bg-primary/5 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className={`mr-1.5 text-xs ${
                        c.group === 'text' ? 'ri-text' :
                        c.group === 'cta' ? 'ri-rectangle-line' :
                        'ri-shape-line'
                      }`} />
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* CENTER — canvas */}
        <main className="col-span-7 flex flex-col min-h-0 bg-white rounded-2xl border border-surface-dark/40 p-4">
          <div className="flex-shrink-0 flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              {selected ? (
                <>
                  <h3 className="font-display font-bold text-dark text-sm truncate">{selected.name}</h3>
                  {builtIn && (
                    <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-surface text-dark-muted flex-shrink-0">
                      {isModified ? 'MODIFIED' : 'FACTORY'}
                    </span>
                  )}
                </>
              ) : (
                <h3 className="font-display font-bold text-dark text-sm">No template selected</h3>
              )}
            </div>
            <span className="text-xs text-dark-muted">
              {layers.length} layer{layers.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Sample Copy bar — stress-test the layout against varied copy
              length. Not saved to templates.json; localStorage-backed so
              edits persist across reloads. */}
          <div className="flex-shrink-0 mb-3 rounded-lg bg-surface/60 border border-surface-dark/30">
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="text-xs font-bold text-dark-muted uppercase tracking-wide flex-shrink-0">Sample Copy</span>
              <div className="flex gap-1 flex-1 min-w-0 overflow-x-auto">
                {Object.entries(SAMPLE_COPY_PRESETS).map(([label, copy]) => (
                  <button
                    key={label}
                    onClick={() => setSampleCopy(copy)}
                    className="flex-shrink-0 px-2 py-1 rounded-md bg-white border border-surface-dark/40 text-xs font-semibold text-dark hover:border-primary hover:text-primary transition-colors cursor-pointer"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setSampleCopyOpen((v) => !v)}
                className="flex-shrink-0 px-2 py-1 rounded-md text-xs font-semibold text-dark-muted hover:text-dark hover:bg-white transition-colors cursor-pointer"
              >
                <i className={`mr-1 ${sampleCopyOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                {sampleCopyOpen ? 'Collapse' : 'Edit'}
              </button>
            </div>
            {sampleCopyOpen && (
              <div className="grid grid-cols-2 gap-2 px-3 pb-3">
                {(['headline', 'tagline', 'subtext', 'cta'] as const).map((field) => (
                  <label key={field} className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-dark-muted uppercase tracking-wide">{field}</span>
                    <input
                      type="text"
                      value={sampleCopy[field]}
                      onChange={(e) => setSampleCopy({ ...sampleCopy, [field]: e.target.value })}
                      className="px-2 py-1 rounded-md border border-surface-dark/40 bg-white text-xs text-dark focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    />
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 flex items-center justify-center">
            <div
              ref={canvasRef}
              onClick={() => setSelectedLayerIdx(null)}
              className={`relative max-w-2xl w-full ${aspectClass(aspectRatio)} rounded-xl overflow-hidden border border-surface-dark/30 bg-white`}
              style={{
                backgroundImage: `url(${SAMPLE_BG})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {resolvedLayers.map((resolved, idx) => (
                <DraggableLayer
                  key={idx}
                  layer={resolved}
                  idx={idx}
                  isSelected={selectedLayerIdx === idx}
                  isDragging={draggingIdx === idx}
                  previewFontScale={previewFontScale}
                  onSelect={(e) => {
                    e.stopPropagation();
                    setSelectedLayerIdx(idx);
                  }}
                  onDragStart={(e, boxW, boxH, cursorX, cursorY) => {
                    e.stopPropagation();
                    dragBoxRef.current = { w: boxW, h: boxH };
                    // Use the RESOLVED position so dragging a stacked layer
                    // doesn't jump to its stale authored y on first move.
                    dragOffsetRef.current = { dx: cursorX - resolved.x, dy: cursorY - resolved.y };
                    if (resolved.kind === 'text' && resolved.stackOnPrevious) {
                      handleBreakStack(idx, resolved.y);
                    }
                    setSelectedLayerIdx(idx);
                    setDraggingIdx(idx);
                  }}
                />
              ))}
              {!selected && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-sm">
                  Pick or create a template on the right →
                </div>
              )}
            </div>
          </div>

          {/* Layer toolbar */}
          {selectedLayerIdx !== null && layers[selectedLayerIdx] && (() => {
            const sel = layers[selectedLayerIdx];
            const selText = sel.kind === 'text' ? sel : null;
            const canCouple = selText !== null && hasPreviousTextLayer(selectedLayerIdx);
            return (
              <div className="flex-shrink-0 mt-3 flex items-center gap-3 px-3 py-2 rounded-lg bg-surface border border-surface-dark/30 flex-wrap">
                <span className="text-xs text-dark-muted">
                  Layer {selectedLayerIdx + 1} — {sel.kind === 'text'
                    ? (sel as { role?: string }).role || 'text'
                    : (sel as ShapeLayer).shape}
                </span>
                {canCouple && selText && (
                  <>
                    <label className="flex items-center gap-1.5 text-xs text-dark cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!selText.stackOnPrevious}
                        onChange={() => handleToggleCoupled(selectedLayerIdx)}
                        className="accent-primary cursor-pointer"
                      />
                      <i className="ri-link text-xs" />
                      Coupled
                    </label>
                    {selText.stackOnPrevious && (
                      <label className="flex items-center gap-1 text-xs text-dark-muted">
                        Gap
                        <input
                          type="number"
                          min={0}
                          max={400}
                          value={selText.stackGap ?? 24}
                          onChange={(e) => handleSetGap(selectedLayerIdx, Number(e.target.value) || 0)}
                          className="w-14 px-1.5 py-0.5 rounded border border-surface-dark/40 bg-white text-xs text-dark text-right focus:outline-none focus:border-primary"
                        />
                        <span className="text-xs">px</span>
                      </label>
                    )}
                  </>
                )}
                <div className="ml-auto flex items-center gap-1">
                  <div className="flex items-center rounded-md border border-surface-dark/40 overflow-hidden">
                    <button
                      onClick={() => handleHorizontalAlign(selectedLayerIdx, 'l')}
                      title="Left-align — bar anchors at the left safe margin"
                      className="px-2 py-1 text-xs text-dark hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      <i className="ri-align-left" />
                    </button>
                    <button
                      onClick={() => handleHorizontalAlign(selectedLayerIdx, 'c')}
                      title="Center — bar anchors at x=0.5, grows equally L/R"
                      className="px-2 py-1 text-xs text-dark hover:bg-primary/10 hover:text-primary transition-colors border-l border-surface-dark/40"
                    >
                      <i className="ri-align-center" />
                    </button>
                    <button
                      onClick={() => handleHorizontalAlign(selectedLayerIdx, 'r')}
                      title="Right-align — bar anchors at the right safe margin"
                      className="px-2 py-1 text-xs text-dark hover:bg-primary/10 hover:text-primary transition-colors border-l border-surface-dark/40"
                    >
                      <i className="ri-align-right" />
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeleteLayer(selectedLayerIdx)}
                    className="px-2 py-1 rounded-md text-xs text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <i className="ri-delete-bin-line mr-1" />Delete
                  </button>
                </div>
              </div>
            );
          })()}
        </main>

        {/* RIGHT — template list */}
        <aside className="col-span-3 flex flex-col min-h-0 bg-white rounded-2xl border border-surface-dark/40 overflow-hidden">
          <div className="flex-shrink-0 px-4 py-3 border-b border-surface-dark/30 flex items-center justify-between">
            <h3 className="font-display font-bold text-dark text-xs">Templates ({templates.length})</h3>
            <button
              onClick={handleNewTemplate}
              className="px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 cursor-pointer"
            >
              <i className="ri-add-line mr-1" />New
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1">
            {templates.map((t, idx) => {
              const factory = BUILT_IN_PRESETS.find((p) => p.id === t.id);
              const userModified = factory && JSON.stringify(factory) !== JSON.stringify(t);
              const isUserOnly = !factory;
              const isRenaming = renamingId === t.id;
              const isDragSource = dragSrcIdx === idx;
              const isDragOver = dragOverIdx === idx && dragSrcIdx !== null && dragSrcIdx !== idx;
              return (
                <div
                  key={t.id}
                  draggable={!isRenaming}
                  onDragStart={handleRowDragStart(idx)}
                  onDragOver={handleRowDragOver(idx)}
                  onDrop={handleRowDrop(idx)}
                  onDragEnd={handleRowDragEnd}
                  className={`group flex items-center gap-1.5 px-2 py-2 rounded-lg transition-all ${
                    isRenaming ? 'cursor-default' : 'cursor-pointer'
                  } ${
                    selectedId === t.id ? 'bg-primary/5 border border-primary/40' : 'border border-transparent hover:bg-surface'
                  } ${
                    isDragSource ? 'opacity-40' : ''
                  } ${
                    isDragOver ? 'bg-primary/15 border-primary/60' : ''
                  }`}
                  onClick={() => {
                    if (isRenaming) return;
                    setSelectedId(t.id);
                    setSelectedLayerIdx(null);
                  }}
                >
                  {/* Drag handle — visual cue + cursor change. The whole row
                      is technically draggable, but the grip makes it obvious. */}
                  <i
                    className="ri-draggable text-dark-muted/60 text-base flex-shrink-0 cursor-grab"
                    title="Drag to reorder"
                  />

                  <div className="flex-1 min-w-0">
                    {isRenaming ? (
                      <input
                        autoFocus
                        defaultValue={t.name}
                        onFocus={(e) => e.currentTarget.select()}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => handleSaveRename(t.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRename(t.id, (e.target as HTMLInputElement).value);
                          else if (e.key === 'Escape') handleCancelRename();
                        }}
                        className="w-full px-1.5 py-0.5 rounded border border-primary bg-white text-xs font-semibold text-dark focus:outline-none focus:ring-1 focus:ring-primary/20"
                      />
                    ) : (
                      <p className="text-xs font-semibold text-dark truncate">{t.name}</p>
                    )}
                    <p className="text-xs text-dark-muted">
                      {t.layouts['1:1']?.layers.length || t.layouts['4:5']?.layers.length || 0} layers
                      {userModified && <span className="ml-1 text-amber-600">· modified</span>}
                      {isUserOnly && <span className="ml-1 text-emerald-600">· custom</span>}
                    </p>
                  </div>

                  {/* Action buttons — hover-revealed to keep the list calm. */}
                  {!isRenaming && (
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartRename(t.id); }}
                        title="Rename"
                        className="px-1.5 py-1 rounded text-dark-muted hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                      >
                        <i className="ri-pencil-line text-xs" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDuplicateTemplate(t.id); }}
                        title="Duplicate"
                        className="px-1.5 py-1 rounded text-dark-muted hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                      >
                        <i className="ri-file-copy-line text-xs" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }}
                        title="Delete"
                        className="px-1.5 py-1 rounded text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {builtIn && isModified && (
            <div className="flex-shrink-0 px-3 py-2 border-t border-surface-dark/30">
              <button
                onClick={handleResetToBuiltIn}
                className="w-full px-2 py-1.5 rounded-md border border-surface-dark/40 text-xs font-semibold text-dark-muted hover:text-dark hover:border-dark/30 transition-colors"
              >
                <i className="ri-restart-line mr-1" />Reset "{builtIn.name}" to factory
              </button>
            </div>
          )}
          {/* Hint */}
          <div className="flex-shrink-0 px-3 py-2 border-t border-surface-dark/30 bg-surface/50">
            <p className="text-xs text-dark-muted leading-tight">
              Save writes to <code className="font-mono">templates.json</code>. Built-ins remain in code as fallback.
            </p>
            {/* newName is reserved for a future "save as" flow, suppress unused warning */}
            <input type="hidden" value={newName} onChange={(e) => setNewName(e.target.value)} />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---- Draggable layer renderer ----
// Takes a fully-resolved Layer (text already populated, stacking already
// applied) — the parent runs the same render pipeline as production.

interface DraggableLayerProps {
  layer: Layer;
  idx: number;
  isSelected: boolean;
  isDragging: boolean;
  previewFontScale: number;
  onSelect: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragStart: (
    e: React.MouseEvent | React.TouchEvent,
    boxW: number,
    boxH: number,
    cursorX: number,
    cursorY: number,
  ) => void;
}

function DraggableLayer({
  layer,
  idx,
  isSelected,
  isDragging,
  previewFontScale,
  onSelect,
  onDragStart,
}: DraggableLayerProps) {
  // Note: idx is in scope for keying / debugging, not used in markup directly.
  void idx;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const parentRect = (e.currentTarget.parentElement as HTMLElement | null)?.getBoundingClientRect();
    if (!parentRect) return;
    const cursorX = (e.clientX - parentRect.left) / parentRect.width;
    const cursorY = (e.clientY - parentRect.top) / parentRect.height;
    onDragStart(e, r.width, r.height, cursorX, cursorY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const parentRect = (e.currentTarget.parentElement as HTMLElement | null)?.getBoundingClientRect();
    const t = e.touches[0];
    if (!t || !parentRect) return;
    const cursorX = (t.clientX - parentRect.left) / parentRect.width;
    const cursorY = (t.clientY - parentRect.top) / parentRect.height;
    onDragStart(e, r.width, r.height, cursorX, cursorY);
  };

  const ring = isSelected ? 'ring-2 ring-primary ring-offset-1 ring-offset-transparent' : '';
  const cursor = isDragging ? 'cursor-grabbing' : 'cursor-grab';

  if (layer.kind === 'shape') {
    return (
      <div
        onClick={onSelect}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className={`absolute select-none ${cursor} ${ring}`}
        style={{
          left: `${layer.x * 100}%`,
          top: `${layer.y * 100}%`,
          width: `${layer.w * 100}%`,
          height: `${layer.h * 100}%`,
          backgroundColor: layer.color,
          opacity: layer.opacity,
          borderRadius: layer.shape === 'circle' ? '50%' : undefined,
          transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
          touchAction: 'none',
        }}
      />
    );
  }

  return (
    <div
      onClick={onSelect}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={`absolute select-none ${cursor} ${ring}`}
      style={getTextLayerStyle(layer, { previewFontScale })}
    >
      {layer.text || <span className="opacity-40 italic">empty</span>}
    </div>
  );
}

